import React, { useState, useEffect, useRef } from "react";
import { useDayPlan, PER_PERSON_UNIT } from "../../DayPlanProvider";
import { CategoryOptionResponse } from "../../types/dayPlan.types";
import { getCategoryOptionsByType } from "../../../../services/api";
import { useToast } from "../../../../contexts/ToastContext";

interface CategoryDropdownProps {
  categoryType: "gastronomy" | "conference" | "additional";
  onClose: () => void;
}

export const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  categoryType,
  onClose,
}) => {
  const { addPlanItem, numberOfGuests } = useDayPlan();
  const { addToast } = useToast();
  const [options, setOptions] = useState<CategoryOptionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const categoryOptions = await getCategoryOptionsByType(categoryType);
        setOptions(categoryOptions);
      } catch (error) {
        console.error("Failed to fetch category options:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [categoryType]);

  useEffect(() => {
    // Auto-focus search input when dropdown opens
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const filteredOptions = options.filter((option) => {
    const query = searchQuery.toLowerCase();
    const matchesName = option.name?.toLowerCase().includes(query);
    const matchesDescription = option.description
      ?.toLowerCase()
      .includes(query);
    return matchesName || matchesDescription;
  });

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  const handleOptionClick = async (option: CategoryOptionResponse) => {
    try {
      // Use numberOfGuests for per-person items: explicit unit "os." OR gastronomy without unit set
      const isPerPerson =
        option.unit === PER_PERSON_UNIT ||
        (!option.unit && option.optionType === "gastronomy");
      const quantity = isPerPerson && numberOfGuests ? numberOfGuests : 1;

      await addPlanItem({
        categoryOptionId: option.id,
        quantity: quantity,
      });

      onClose();
    } catch (error) {
      console.error("Failed to add category option:", error);
      addToast({ type: "error", message: "Nie udało się dodać elementu" });
    }
  };

  if (isLoading) {
    return (
      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="text-center text-gray-500">Ładowanie...</div>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="text-center text-gray-500">Brak dostępnych opcji</div>
      </div>
    );
  }

  return (
    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden flex flex-col">
      {/* Search Input */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-3">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                onClose();
              }
            }}
            placeholder="Szukaj..."
            className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Options List */}
      <div className="overflow-y-auto flex-1">
        {filteredOptions.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            Nie znaleziono wyników
          </div>
        ) : (
          filteredOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="font-medium text-gray-900">
                {highlightText(option.name, searchQuery)}
              </div>
              {option.description && (
                <div className="text-xs text-gray-500 mt-1">
                  {highlightText(option.description, searchQuery)}
                </div>
              )}
              {option.basePrice && (
                <div className="text-xs text-gray-600 mt-1">
                  od {option.basePrice} zł
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
};
